import { reellTimepris } from "@/lib/project-rates";
import { budsjettavvikPct } from "@/lib/completed-projects";
import { feilbudsjettering, type DashboardProject, type FeilbudsjetteringResult } from "@/lib/dashboard-bf-calc";
import { isSalesProject } from "@/lib/offer-closed-date";
import type { DashboardPeriod } from "@/lib/dashboard-period";
import type { DBInvoice, DBProject } from "@/lib/db-types";

/** Hele historikken: feilbudsjettering(…) filtrerer på periode, kundekortet vil ha alt. */
const ALL_TIME: DashboardPeriod = {
  granularity: "custom",
  year: 1970,
  customFrom: "1900-01-01",
  customTo: "2999-12-31",
};

export interface CustomerKpis {
  /** Sum kontraktssum for kundens ikke-interne, ikke-kansellerte prosjekter. null uten slike. */
  valueSold: number | null;
  /** Sum bf_actual for avsluttede prosjekter. null uten avsluttede. */
  bfActual: number | null;
  /** reellTimepris(bf_actual, timer) over avsluttede prosjekter. null uten grunnlag. */
  realHourlyRate: number | null;
  /** Avvik mellom estimert og faktisk BF (feilbudsjettering). null uten avsluttede prosjekter med estimat. */
  deviation: FeilbudsjetteringResult | null;
  completedCount: number;
}

type KpiProject = Pick<
  DBProject,
  "id" | "total_amount" | "bf_estimated" | "bf_actual" | "estimated_deadline" | "created_at" | "customer_id"
> & {
  is_internal: boolean | null;
  project_status: string;
  completed_at: string | null;
};

/**
 * Nøkkeltall for kundekortet. Ingen ny formel: BF reell, timepris og avvik
 * bruker de samme funksjonene som dashboardet (`reellTimepris`,
 * `feilbudsjettering`). Manglende grunnlag gir null, som vises som «–», aldri 0.
 */
export function computeCustomerKpis(
  projects: readonly KpiProject[],
  hoursByProject: ReadonlyMap<string, number>,
): CustomerKpis {
  const sales = projects.filter((p) => isSalesProject(p) && p.project_status !== "cancelled");
  const completed = sales.filter((p) => p.project_status === "completed");

  const valueSold = sales.length > 0 ? sales.reduce((s, p) => s + (p.total_amount ?? 0), 0) : null;
  const bfSum = completed.reduce((s, p) => s + (p.bf_actual ?? 0), 0);
  const hoursSum = completed.reduce((s, p) => s + (hoursByProject.get(p.id) ?? 0), 0);
  const completedContract = completed.reduce((s, p) => s + (p.total_amount ?? 0), 0);
  const hasBf = completed.some((p) => p.bf_actual != null);

  const deviation = completed.length > 0
    ? feilbudsjettering(sales.map(toDashboardProject), [], ALL_TIME, {})
    : null;

  return {
    valueSold,
    bfActual: hasBf ? bfSum : null,
    realHourlyRate: completed.length > 0 ? reellTimepris(bfSum, hoursSum) : null,
    deviation: deviation && deviation.projectCount > 0 ? deviation : null,
    completedCount: completed.length,
  };
}

function toDashboardProject(p: KpiProject): DashboardProject {
  return {
    id: p.id,
    is_internal: p.is_internal,
    project_status: p.project_status,
    total_amount: p.total_amount,
    bf_estimated: p.bf_estimated,
    bf_actual: p.bf_actual,
    completed_at: p.completed_at,
    created_at: p.created_at,
    estimated_deadline: p.estimated_deadline,
    customer_id: p.customer_id,
  };
}

// ── Kundekort v2 (wireframe-justering 19.10) ────────────────────────────

const DAY_MS = 24 * 60 * 60 * 1000;

/** Hele dager fra `today` (YYYY-MM-DD eller ISO) til `date`. Negativt = passert. */
export function daysUntil(date: string, today: Date): number {
  const target = new Date(date.length === 10 ? `${date}T00:00:00Z` : date).getTime();
  const base = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  return Math.round((target - base) / DAY_MS);
}

/** «Kunde siden»: året for kundens eldste prosjekt. null uten prosjekter. */
export function customerSinceYear(projects: ReadonlyArray<Pick<DBProject, "created_at">>): number | null {
  if (projects.length === 0) return null;
  const earliest = projects.reduce((min, p) => (p.created_at < min ? p.created_at : min), projects[0].created_at);
  const year = new Date(earliest).getUTCFullYear();
  return Number.isNaN(year) ? null : year;
}

export interface CustomerProjectRow {
  id: string;
  name: string;
  projectCode: string | null;
  status: string;
  /** Leveringsdato (completed_at) for avsluttede, ellers null. */
  deliveredAt: string | null;
  contractKr: number | null;
  /** bf_actual i kroner for avsluttede prosjekter. null uten grunnlag. */
  bfActualKr: number | null;
  /** reellTimepris(bf_actual, loggede timer). null uten grunnlag. */
  realHourlyRate: number | null;
  /**
   * Budsjettavvik i timer per prosjekt: (brukt − solgt) / solgt × 100, samme
   * tall som avsluttet-lista (SOT «Budsjetteringsavvik B», `budsjettavvikPct`).
   * Positivt = brukte flere timer enn solgt. null for prosjekter som ikke er
   * avsluttet eller mangler solgte timer.
   */
  deviationPct: number | null;
}

type RowProject = Pick<
  DBProject,
  "id" | "name" | "project_code" | "total_amount" | "bf_actual" | "created_at"
> & { is_internal: boolean | null; project_status: string | null; completed_at: string | null };

function roundOne(n: number | null): number | null {
  return n == null ? null : Math.round(n * 10) / 10;
}

/**
 * Radene i «Det vi har laget»: salgsprosjekter som ikke er kansellert, pågående
 * først (nyeste øverst), så avsluttede etter leveringsdato. Samme formler som
 * nøkkeltallene over, bare per prosjekt.
 */
export function customerProjectRows(
  projects: readonly RowProject[],
  hoursByProject: ReadonlyMap<string, number>,
  budgetedByProject: ReadonlyMap<string, number>,
): CustomerProjectRow[] {
  const sales = projects.filter((p) => isSalesProject(p) && p.project_status !== "cancelled");
  const rows = sales.map<CustomerProjectRow>((p) => {
    const completed = p.project_status === "completed";
    const contract = p.total_amount != null ? Number(p.total_amount) : null;
    const bf = completed && p.bf_actual != null ? Number(p.bf_actual) : null;
    return {
      id: p.id,
      name: p.name,
      projectCode: p.project_code,
      status: p.project_status ?? "",
      deliveredAt: completed ? p.completed_at : null,
      contractKr: contract,
      bfActualKr: bf != null ? Math.round(bf) : null,
      realHourlyRate: completed ? reellTimepris(p.bf_actual, hoursByProject.get(p.id) ?? 0) : null,
      deviationPct: completed ? roundOne(budsjettavvikPct(budgetedByProject.get(p.id) ?? 0, hoursByProject.get(p.id) ?? 0)) : null,
    };
  });
  const byId = new Map(sales.map((p) => [p.id, p]));
  return rows.sort((a, b) => {
    const aDone = a.deliveredAt != null;
    const bDone = b.deliveredAt != null;
    if (aDone !== bDone) return aDone ? 1 : -1;
    const aKey = aDone ? a.deliveredAt! : byId.get(a.id)!.created_at;
    const bKey = bDone ? b.deliveredAt! : byId.get(b.id)!.created_at;
    return bKey.localeCompare(aKey);
  });
}

export interface OutstandingInvoices {
  count: number;
  totalKr: number;
  /** Tidligste forfallsdato blant de utestående. null når ingen har forfall. */
  earliestDueDate: string | null;
}

const OUTSTANDING_STATUSES = new Set(["sent", "overdue"]);

/** Utestående: sendte eller forfalte fakturaer uten betalingsdato. */
export function outstandingInvoices(
  invoices: ReadonlyArray<Pick<DBInvoice, "status" | "amount" | "due_date" | "paid_date">>,
): OutstandingInvoices {
  const open = invoices.filter((i) => OUTSTANDING_STATUSES.has(i.status ?? "") && !i.paid_date);
  const dues = open.map((i) => i.due_date).filter((d): d is string => !!d).sort();
  return {
    count: open.length,
    totalKr: Math.round(open.reduce((s, i) => s + Number(i.amount ?? 0), 0)),
    earliestDueDate: dues[0] ?? null,
  };
}

/** Tidligste forventede close-dato blant tilbudene. null når ingen har dato. */
export function nextCloseDate(offers: ReadonlyArray<{ expected_close_date: string | null }>): string | null {
  const dates = offers.map((o) => o.expected_close_date).filter((d): d is string => !!d).sort();
  return dates[0] ?? null;
}

// ── Kundekort v3: GEEK-baserte nøkkeltall ────────────────────────────────

export interface CustomerGeek {
  /** Σ bf_actual på avsluttede salgsprosjekter («GEEK levert»). null uten avsluttede med tall. */
  deliveredKr: number | null;
  /** Kontraktssum på de avsluttede prosjektene som inngår i deliveredKr. */
  deliveredContractKr: number;
  deliveredCount: number;
  /** Σ bf_estimated på pågående og kommende salgsprosjekter («GEEK i arbeid»), samme som pipelineBf. */
  inProgressKr: number | null;
  inProgressCount: number;
  /** GEEK per logget time på avsluttede prosjekter (reellTimepris). */
  realHourlyRate: number | null;
}

/**
 * GEEK-stripen på kundekortet. Ingen ny formel: levert = bf_actual, i arbeid =
 * bf_estimated (som `pipelineBf` på salgsdashboardet), timepris = `reellTimepris`.
 */
export function customerGeek(
  projects: readonly KpiProject[],
  hoursByProject: ReadonlyMap<string, number>,
): CustomerGeek {
  const sales = projects.filter((p) => isSalesProject(p) && p.project_status !== "cancelled");
  const completed = sales.filter((p) => p.project_status === "completed");
  const inProgress = sales.filter((p) => p.project_status === "active" || p.project_status === "kommende");

  const hasBf = completed.some((p) => p.bf_actual != null);
  const deliveredKr = completed.reduce((s, p) => s + Number(p.bf_actual ?? 0), 0);
  const deliveredContractKr = completed.reduce((s, p) => s + Number(p.total_amount ?? 0), 0);
  const hoursSum = completed.reduce((s, p) => s + (hoursByProject.get(p.id) ?? 0), 0);
  const inProgressKr = inProgress.reduce((s, p) => s + Number(p.bf_estimated ?? 0), 0);

  return {
    deliveredKr: hasBf ? Math.round(deliveredKr) : null,
    deliveredContractKr: Math.round(deliveredContractKr),
    deliveredCount: completed.length,
    inProgressKr: inProgress.some((p) => p.bf_estimated != null) ? Math.round(inProgressKr) : null,
    inProgressCount: inProgress.length,
    realHourlyRate: completed.length > 0 ? reellTimepris(deliveredKr, hoursSum) : null,
  };
}

export interface LastPurchase {
  /** Prosjektets opprettelsesdato, samme hendelse som kundestatusen regner «siste kjøp» fra. */
  at: string;
  projectId: string;
  name: string;
  contractKr: number | null;
}

/** «Siste kjøp» i heroen: nyeste ikke-interne, ikke-kansellerte prosjekt. null uten prosjekter. */
export function lastPurchase(
  projects: ReadonlyArray<Pick<DBProject, "id" | "name" | "total_amount" | "created_at"> & { is_internal: boolean | null; project_status: string | null }>,
): LastPurchase | null {
  const sales = projects.filter((p) => isSalesProject(p) && p.project_status !== "cancelled");
  if (sales.length === 0) return null;
  const latest = sales.reduce((best, p) => (p.created_at > best.created_at ? p : best), sales[0]);
  return {
    at: latest.created_at,
    projectId: latest.id,
    name: latest.name,
    contractKr: latest.total_amount != null ? Number(latest.total_amount) : null,
  };
}
